import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HubIcon from '@mui/icons-material/Hub';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';

// SegmentPlansPanel — the "Segment Plans" sub-tab inside Plans. Lists
// long-lived segment rollouts (offline-capable, two-phase: seed segment
// + FEFE failsafe, then strip the failsafe once every member is online)
// and lets the operator watch per-device phase progress and cancel.

const POLL_INTERVAL_MS = 15000;

const STATUS_META = {
  phase1: { label: 'Phase 1 — seeding', color: 'info', icon: <HourglassTopIcon fontSize="small" /> },
  phase2: { label: 'Phase 2 — finalizing', color: 'info', icon: <HourglassTopIcon fontSize="small" /> },
  done: { label: 'Done', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  cancelling: { label: 'Cancelling — rolling back', color: 'warning', icon: <HourglassTopIcon fontSize="small" /> },
  cancelled: { label: 'Cancelled', color: 'default', icon: <StopCircleIcon fontSize="small" /> },
  failed: { label: 'Failed', color: 'error', icon: <CancelIcon fontSize="small" /> },
};

// Plans still in flight (rolling out or rolling back). "cancelling" is
// active so it keeps polling and is counted, but the Cancel button is
// hidden for it (see allowCancel gate below).
const ACTIVE_STATUSES = new Set(['phase1', 'phase2', 'cancelling']);
const CANCELLABLE_STATUSES = new Set(['phase1', 'phase2']);

const PHASE_TOOLTIP =
  'Two-phase rollout. Phase 1 writes the new segment + a 0xFEFE "accept any child" failsafe to every member (online now, offline on reconnect) so nothing is orphaned. Phase 2 strips the failsafe once every member has confirmed phase 1. The plan runs until every member — including offline ones — is done.';

const statusChip = (status) => {
  const meta = STATUS_META[status] || { label: status || 'unknown', color: 'default' };
  return <Chip size="small" color={meta.color} icon={meta.icon} label={meta.label} />;
};

const phaseProgress = (plan, phase) => {
  const total = plan?.summary?.total ?? (plan?.devices || []).length;
  const done = phase === 2 ? plan?.summary?.phase2_complete ?? 0 : plan?.summary?.phase1_complete ?? 0;
  return { done, total };
};

const fmtTime = (t) => {
  if (!t) return '—';
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const deviceStateChip = (state) => {
  if (state === 'complete') {
    return <Chip size="small" color="success" variant="outlined" label="complete" />;
  }
  return <Chip size="small" color="warning" variant="outlined" label="pending" />;
};

const SegmentPlanDetail = ({ plan, allowCancel, onCancel, cancelling, onDropDevice, droppingSerial }) => {
  if (!plan) return null;
  const p1 = phaseProgress(plan, 1);
  const p2 = phaseProgress(plan, 2);
  const devices = plan.devices || [];
  const canEdit = allowCancel && CANCELLABLE_STATUSES.has(plan.status);
  const canDrop = canEdit && devices.length > 1;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        {statusChip(plan.status)}
        <Chip size="small" variant="outlined" label={`segment ${plan.segment_id}`} />
        <Chip size="small" variant="outlined" label={`phase 1: ${p1.done}/${p1.total}`} />
        <Chip size="small" variant="outlined" label={`phase 2: ${p2.done}/${p2.total}`} />
        {plan.summary?.offline_pending > 0 && (
          <Tooltip title="Members still offline; the plan applies their config when they reconnect.">
            <Chip size="small" color="warning" variant="outlined" label={`${plan.summary.offline_pending} offline pending`} />
          </Tooltip>
        )}
      </Stack>

      {plan.last_error && (
        <Alert severity="error" sx={{ mb: 1 }}>{plan.last_error}</Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Created {fmtTime(plan.created_at)}{plan.completed_at ? ` · finished ${fmtTime(plan.completed_at)}` : ''}
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phase 1</TableCell>
              <TableCell>Phase 2</TableCell>
              <TableCell>Note</TableCell>
              {canEdit && <TableCell align="right">Drop</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((d) => (
              <TableRow key={d.serial} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{d.serial}</TableCell>
                <TableCell>{d.is_cap ? 'CAP' : 'Lamp'}</TableCell>
                <TableCell>{deviceStateChip(d.phase1_state)}</TableCell>
                <TableCell>{deviceStateChip(d.phase2_state)}</TableCell>
                <TableCell sx={{ color: 'error.main', fontSize: 12 }}>{d.last_error || ''}</TableCell>
                {canEdit && (
                  <TableCell align="right">
                    <Tooltip title={canDrop
                      ? 'Remove this device from the rollout (e.g. decommissioned or permanently offline). Its segment is rolled back and the plan continues without it.'
                      : 'Cannot drop the last device — cancel the whole rollout instead.'}>
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          disabled={!canDrop || droppingSerial === d.serial}
                          onClick={() => onDropDevice(plan.id, d.serial)}
                        >
                          {droppingSerial === d.serial
                            ? <CircularProgress size={16} color="inherit" />
                            : <RemoveCircleOutlineIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {devices.length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5}>
                  <Typography variant="body2" color="text.secondary">No devices in this plan.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {allowCancel && CANCELLABLE_STATUSES.has(plan.status) && (
        <Box sx={{ mt: 2 }}>
          <Button
            color="error"
            variant="outlined"
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <StopCircleIcon />}
            disabled={cancelling}
            onClick={() => onCancel(plan.id)}
          >
            Cancel rollout (roll back segment)
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Removes the segment (and failsafe) from every member — online now, offline on reconnect — and deletes the tree.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SegmentPlansPanel = ({ isActive = true, allowCancel = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [droppingSerial, setDroppingSerial] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listSegmentPlans();
      setPlans(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Failed to load segment plans');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshDetail = useCallback(async (planId) => {
    if (!planId) return;
    try {
      const p = await api.getSegmentPlan(planId);
      setDetail(p);
    } catch (e) {
      setError(e?.message || 'Failed to load segment plan');
    }
  }, []);

  // Poll the list while the tab is active (plans advance as devices
  // come online over time).
  useEffect(() => {
    if (!isActive) return undefined;
    refresh();
    pollRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isActive, refresh]);

  // Poll the open detail while it's an active rollout.
  useEffect(() => {
    if (!selectedId) return undefined;
    refreshDetail(selectedId);
    const id = setInterval(() => refreshDetail(selectedId), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [selectedId, refreshDetail]);

  const handleCancel = useCallback(async (planId) => {
    setCancelling(true);
    try {
      await api.cancelSegmentPlan(planId);
      enqueueSnackbar('Segment rollout cancelled; rolling back.', { variant: 'success' });
      await refresh();
      await refreshDetail(planId);
    } catch (e) {
      enqueueSnackbar(e?.message || 'Failed to cancel rollout', { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  }, [enqueueSnackbar, refresh, refreshDetail]);

  const handleDropDevice = useCallback(async (planId, serial) => {
    setDroppingSerial(serial);
    try {
      await api.dropSegmentPlanDevice(planId, serial);
      enqueueSnackbar(`Removed ${serial} from the rollout.`, { variant: 'success' });
      await refresh();
      await refreshDetail(planId);
    } catch (e) {
      enqueueSnackbar(e?.message || `Failed to drop ${serial}`, { variant: 'error' });
    } finally {
      setDroppingSerial(null);
    }
  }, [enqueueSnackbar, refresh, refreshDetail]);

  const activeCount = useMemo(
    () => plans.filter((p) => ACTIVE_STATUSES.has(p.status)).length,
    [plans],
  );

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <HubIcon color="action" />
        <Typography variant="h6" sx={{ flexGrow: 0 }}>Segment Plans</Typography>
        <Tooltip title={PHASE_TOOLTIP}>
          <Chip size="small" variant="outlined" label={`${activeCount} active`} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={refresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Long-lived segment rollouts created from the Segment tab. Each runs until every member — including offline devices — has the segment. {' '}
        <Tooltip title={PHASE_TOOLTIP}><span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>How it works</span></Tooltip>
      </Typography>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Phase 1</TableCell>
              <TableCell>Phase 2</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => {
              const p1 = phaseProgress(plan, 1);
              const p2 = phaseProgress(plan, 2);
              return (
                <TableRow key={plan.id} hover>
                  <TableCell>{plan.name || plan.id}</TableCell>
                  <TableCell>{statusChip(plan.status)}</TableCell>
                  <TableCell>{`${p1.done}/${p1.total}`}</TableCell>
                  <TableCell>{`${p2.done}/${p2.total}`}</TableCell>
                  <TableCell>{fmtTime(plan.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View progress">
                      <IconButton size="small" onClick={() => setSelectedId(plan.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {plans.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No segment plans yet. Apply a segment from the Segment tab to start one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selectedId}
        onClose={() => { setSelectedId(null); setDetail(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {detail?.name || 'Segment plan'}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={() => { setSelectedId(null); setDetail(null); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <SegmentPlanDetail
            plan={detail}
            allowCancel={allowCancel}
            onCancel={handleCancel}
            cancelling={cancelling}
            onDropDevice={handleDropDevice}
            droppingSerial={droppingSerial}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelectedId(null); setDetail(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SegmentPlansPanel;
